import { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, TextField, Button, Paper,
  CircularProgress, Alert, Chip, Pagination, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Switch, FormControlLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import { useTranslation } from 'react-i18next';
import type { Donor } from '../components/DonorCard';

const PAGE_SIZE = 10;
const SESSION_KEY = 'admin_token';
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

interface AdminResult {
  data: Donor[];
  meta: { total: number; page: number; limit: number };
}

export default function AdminPage() {
  const { t } = useTranslation();
  const [token, setToken]       = useState(() => sessionStorage.getItem(SESSION_KEY) ?? '');
  const [input, setInput]       = useState('');
  const [authError, setAuthError] = useState('');

  const [donors, setDonors]     = useState<Donor[]>([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchDonors = useCallback(async (targetPage = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/donors?page=${targetPage}&limit=${PAGE_SIZE}`,
        { headers: authHeaders },
      );
      if (res.status === 401) { setToken(''); sessionStorage.removeItem(SESSION_KEY); return; }
      const json: AdminResult = await res.json();
      setDonors(json.data);
      setTotal(json.meta.total);
      setPage(targetPage);
    } catch {
      setError(t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load donors once authenticated
  useEffect(() => {
    if (token) fetchDonors(1);
  }, [token, fetchDonors]);

  const handleLogin = async () => {
    // Probe the API to verify password
    const res = await fetch(`${API_BASE}/api/admin/donors?limit=1`, {
      headers: { Authorization: `Bearer ${input}` },
    });
    if (res.ok) {
      sessionStorage.setItem(SESSION_KEY, input);
      setToken(input);
      setAuthError('');
    } else {
      setAuthError(t('admin.wrong_password'));
    }
  };

  const toggleAvailability = async (donor: Donor) => {
    await fetch(`${API_BASE}/api/admin/donors/${donor.id}`, {
      method: 'PATCH',
      headers: authHeaders,
      body: JSON.stringify({ is_available: !donor.is_available }),
    });
    fetchDonors(page);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await fetch(`${API_BASE}/api/admin/donors/${deleteTarget}`, {
      method: 'DELETE',
      headers: authHeaders,
    });
    setDeleteTarget(null);
    fetchDonors(page);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ── Password gate ─────────────────────────────────────────────────────────
  if (!token) {
    return (
      <Container maxWidth="xs" sx={{ py: 12 }}>
        <Box textAlign="center" mb={4}>
          <LockIcon color="primary" sx={{ fontSize: 64 }} />
          <Typography variant="h5" fontWeight={800} mt={1}>
            {t('admin.title')}
          </Typography>
        </Box>
        <Paper sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('admin.password_label')}
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              error={!!authError}
              helperText={authError}
              autoFocus
            />
            <Button variant="contained" size="large" onClick={handleLogin}>
              {t('admin.login_btn')}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" fontWeight={800}>
          {t('admin.all_donors')} ({total})
        </Typography>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={() => { sessionStorage.removeItem(SESSION_KEY); setToken(''); }}
        >
          Logout
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box textAlign="center" py={8}><CircularProgress /></Box>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  {['Name', 'Blood', 'Phone', 'Location', 'Available', 'Registered', 'Actions'].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {donors.map((donor) => (
                  <TableRow key={donor.id} hover>
                    <TableCell>{donor.full_name}</TableCell>
                    <TableCell>
                      <Chip
                        label={donor.blood_type}
                        size="small"
                        sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 700 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                      {(donor as Donor & { phone?: string }).phone ?? '—'}
                    </TableCell>
                    <TableCell>{donor.governorate}, {donor.city}</TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={donor.is_available}
                            onChange={() => toggleAvailability(donor)}
                            color="success"
                            size="small"
                          />
                        }
                        label=""
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>
                      {(donor as Donor & { created_at?: string }).created_at?.slice(0, 10) ?? '—'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={t('admin.delete')}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(donor.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination — 10 donors per page */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                color="primary"
                shape="rounded"
                onChange={(_, p) => fetchDonors(p)}
              />
            </Box>
          )}
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('admin.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('admin.confirm_delete')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('admin.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>
            {t('admin.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
